import React, { useState, useEffect, useRef } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Send, X, Mail, Paperclip, FileText, Trash2 } from 'lucide-react';
import { buildSignatureHTML } from '../organizer/EmailSignatureEditor';

const token = () => localStorage.getItem('token');

/* ────────────────── Email chip input ────────────────── */
function EmailChips({ emails, onChange, placeholder, testId }) {
  const [input, setInput] = useState('');

  const add = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (!emails.includes(trimmed)) onChange([...emails, trimmed]);
    setInput('');
  };

  const remove = (em) => onChange(emails.filter(e => e !== em));

  return (
    <div className="border border-gray-200 rounded-md p-1.5 min-h-9 flex flex-wrap gap-1 focus-within:ring-1 focus-within:ring-teal-500 focus-within:border-teal-500 bg-white">
      {emails.map(em => (
        <span key={em} className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-xs px-2 py-0.5 rounded-full border border-teal-200">
          {em}
          <button onClick={() => remove(em)} className="hover:text-teal-900" type="button"><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={emails.length === 0 ? placeholder : '+ ekle'}
        className="flex-1 min-w-20 outline-none text-sm px-1 bg-transparent"
        data-testid={testId}
      />
    </div>
  );
}

/* ────────────────── helpers ────────────────── */
function buildDefaultBody(companyName) {
  return `Sayın ${companyName || 'Yetkili'} Yetkilileri,\n\n[İçeriğinizi buraya yazın]\n\nSaygılarımızla,`;
}

function buildContractBody(companyName, standNumber, exhibitionName) {
  const standPart = standNumber && standNumber !== '-' ? `${standNumber} nolu stand` : 'standınız';
  const exhibPart = exhibitionName ? ` (${exhibitionName})` : '';
  return `Sayın ${companyName || 'Yetkili'} Yetkilileri,\n\n${standPart}${exhibPart} için düzenlenen sözleşme ekte yer almaktadır.\n\nSözleşmenizi incelemenizi ve onaylamanızı rica ederiz.\n\nSaygılarımızla,`;
}

function buildStandBody(companyName, standNumber, exhibitionName) {
  const standPart = standNumber && standNumber !== '-' ? `${standNumber} nolu stand` : 'standınız';
  const exhibPart = exhibitionName ? ` — ${exhibitionName}` : '';
  return `Sayın ${companyName || 'Yetkili'} Yetkilileri,\n\n${standPart}${exhibPart} hakkındaki bilgileri aşağıda bulabilirsiniz.\n\nSaygılarımızla,`;
}

/**
 * SendEmailModal
 * Props:
 *   open: bool
 *   onClose: fn
 *   companyId: string
 *   contractId: string | null  (pre-select a contract reference)
 *   standId: string | null     (pre-select a stand reference)
 *   companyName: string | null
 */
export default function SendEmailModal({ open, onClose, companyId, contractId, standId, companyName }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [fromEmail, setFromEmail] = useState('');
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [signature, setSignature] = useState(null);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  // File attachments
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const MAX_FILES = 5;
  const MAX_TOTAL_MB = 50;

  // Reference context
  const [context, setContext] = useState(null); // { contracts: [], stands: [] }
  const [selectedRef, setSelectedRef] = useState('none'); // 'none' | 'contract:{id}' | 'stand:{id}'
  const [attachPdf, setAttachPdf] = useState(false);

  // Derived: selected contract / stand objects
  const selectedContract = selectedRef.startsWith('contract:')
    ? (context?.contracts || []).find(c => c.id === selectedRef.replace('contract:', ''))
    : null;
  const selectedStand = selectedRef.startsWith('stand:')
    ? (context?.stands || []).find(s => s.stand_id === selectedRef.replace('stand:', ''))
    : null;
  const showAttachPdf = !!selectedContract;

  useEffect(() => {
    if (!open || !companyId) return;
    setLoading(true);
    setSelectedRef('none');
    setAttachPdf(false);
    setContext(null);
    setAttachedFiles([]);

    Promise.all([
      axios.get(`${API}/companies/${companyId}`, { headers: { Authorization: `Bearer ${token()}` } }),
      axios.get(`${API}/email-settings`, { headers: { Authorization: `Bearer ${token()}` } }),
      axios.get(`${API}/email-signature`, { headers: { Authorization: `Bearer ${token()}` } }),
      axios.get(`${API}/companies/${companyId}/email-context`, { headers: { Authorization: `Bearer ${token()}` } }),
    ])
      .then(([compRes, settingsRes, sigRes, ctxRes]) => {
        const company = compRes.data;
        const contacts = company.contacts || [];
        const allEmails = contacts.filter(c => c.email).map(c => c.email);
        if (allEmails.length === 0 && company.email) allEmails.push(company.email);
        setToEmails(allEmails);
        setCcEmails([]);
        setBccEmails([]);
        setFromEmail(settingsRes.data.from_email || '');
        setSignature(sigRes.data || null);

        const ctx = ctxRes.data || { contracts: [], stands: [] };
        setContext(ctx);

        // Pre-select reference if provided via props
        if (contractId) {
          const found = (ctx.contracts || []).find(c => c.id === contractId);
          if (found) {
            const refKey = `contract:${contractId}`;
            setSelectedRef(refKey);
            applyContractRef(found, company.name || companyName || '');
            setAttachPdf(true);
            return;
          }
        }
        if (standId) {
          const found = (ctx.stands || []).find(s => s.stand_id === standId);
          if (found) {
            setSelectedRef(`stand:${standId}`);
            applyStandRef(found, company.name || companyName || '');
            return;
          }
        }

        // Default: referans seçilmeden boş bırak (context varsa)
        if (ctx.contracts.length > 0 || ctx.stands.length > 0) {
          setSubject('');
          setBody('');
        } else {
          setSubject(`Bilgi — ${company.name || companyName || ''}`);
          setBody(buildDefaultBody(company.name || companyName || ''));
        }
      })
      .catch(() => toast.error('Veriler yüklenemedi'))
      .finally(() => setLoading(false));
  }, [open, companyId]); // eslint-disable-line

  function applyContractRef(contract, cName) {
    const standNo = contract.stand_number || '-';
    const exhib = contract.exhibition_name || '';
    const standPart = standNo !== '-' ? standNo : '';
    const subjectParts = ['Sözleşme', cName, standPart, exhib].filter(Boolean);
    setSubject(subjectParts.join(' — '));
    setBody(buildContractBody(cName, standNo, exhib));
    setAttachPdf(true);
  }

  function applyStandRef(stand, cName) {
    const standNo = stand.stand_number || '-';
    const exhib = stand.exhibition_name || '';
    const standPart = standNo !== '-' ? standNo : '';
    const subjectParts = ['Stand Bilgisi', cName, standPart, exhib].filter(Boolean);
    setSubject(subjectParts.join(' — '));
    setBody(buildStandBody(cName, standNo, exhib));
  }

  const handleRefChange = (val) => {
    setSelectedRef(val);
    setAttachPdf(false);
    const cName = companyName || '';

    if (val === 'none' || !val) {
      setSubject('');
      setBody('');
      return;
    }

    if (val.startsWith('contract:')) {
      const id = val.replace('contract:', '');
      const found = (context?.contracts || []).find(c => c.id === id);
      if (found) applyContractRef(found, cName);
    } else if (val.startsWith('stand:')) {
      const id = val.replace('stand:', '');
      const found = (context?.stands || []).find(s => s.stand_id === id);
      if (found) applyStandRef(found, cName);
    }
  };

  const handleFileAdd = (e) => {
    const files = Array.from(e.target.files || []);
    const currentFiles = [...attachedFiles];
    let totalBytes = currentFiles.reduce((s, f) => s + f.size, 0);
    for (const f of files) {
      if (currentFiles.length >= MAX_FILES) { toast.error(`En fazla ${MAX_FILES} dosya ekleyebilirsiniz`); break; }
      if (totalBytes + f.size > MAX_TOTAL_MB * 1024 * 1024) { toast.error(`Toplam ek boyutu ${MAX_TOTAL_MB}MB'ı geçemez`); break; }
      currentFiles.push(f);
      totalBytes += f.size;
    }
    setAttachedFiles(currentFiles);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx) => setAttachedFiles(prev => prev.filter((_, i) => i !== idx));

  const formatSize = (bytes) => bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  const handleSend = async () => {
    if (toEmails.length === 0) { toast.error('Alıcı adresi giriniz'); return; }
    if (!subject.trim()) { toast.error('Konu giriniz'); return; }
    if (!body.trim()) { toast.error('İçerik giriniz'); return; }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('company_id', companyId);
      if (selectedContract?.id) formData.append('contract_id', selectedContract.id);
      if (selectedStand?.stand_id) formData.append('stand_id', selectedStand.stand_id);
      formData.append('to_emails', JSON.stringify(toEmails));
      formData.append('cc_emails', JSON.stringify(ccEmails));
      formData.append('bcc_emails', JSON.stringify(bccEmails));
      formData.append('subject', subject);
      formData.append('body', body);
      formData.append('attach_contract_pdf', String(showAttachPdf && attachPdf));
      attachedFiles.forEach(f => formData.append('attachments', f));

      await axios.post(`${API}/emails/send`, formData, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success('E-posta gönderildi!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'E-posta gönderilemedi');
    } finally { setSending(false); }
  };

  const sigHtml = buildSignatureHTML(signature);

  const hasContext = context && (context.contracts.length > 0 || context.stands.length > 0);

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-teal-600" />
            E-posta Gönder
            {companyName && <span className="text-gray-400 text-sm font-normal">— {companyName}</span>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Yükleniyor...</div>
        ) : (
          <div className="space-y-3">
            {/* From */}
            {fromEmail ? (
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-xs text-gray-400 w-16 shrink-0">Gönderen</span>
                <span className="text-sm text-gray-700 font-medium">{fromEmail}</span>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                SMTP ayarları yapılandırılmamış. Ayarlar &gt; E-posta Ayarları bölümünden yapılandırın.
              </div>
            )}

            {/* Reference dropdown */}
            {hasContext && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Referans</Label>
                <Select value={selectedRef} onValueChange={handleRefChange}>
                  <SelectTrigger className="text-sm" data-testid="email-ref-select">
                    <SelectValue placeholder="Referans seç (isteğe bağlı)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Referans yok</SelectItem>
                    {context.contracts.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="text-xs text-gray-400">Sözleşmeler</SelectLabel>
                        {context.contracts.map(c => (
                          <SelectItem key={c.id} value={`contract:${c.id}`}>
                            {c.stand_number !== '-' ? `${c.stand_number} — ` : ''}{c.exhibition_name || c.template_name} ({c.status_label})
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {context.stands.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="text-xs text-gray-400">Sözleşmesiz Standlar</SelectLabel>
                        {context.stands.map(s => (
                          <SelectItem key={s.stand_id} value={`stand:${s.stand_id}`}>
                            {s.stand_number !== '-' ? `${s.stand_number} — ` : ''}{s.exhibition_name || ''} ({s.status_label})
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Attach PDF checkbox (only when contract selected) */}
            {showAttachPdf && (
              <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
                <Checkbox
                  id="attach-pdf"
                  checked={attachPdf}
                  onCheckedChange={setAttachPdf}
                  data-testid="email-attach-pdf-checkbox"
                />
                <label htmlFor="attach-pdf" className="text-xs text-teal-700 cursor-pointer flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" />
                  Sözleşme PDF'ini ekle
                  {selectedContract && (
                    <span className="text-teal-500">
                      ({selectedContract.stand_number !== '-' ? selectedContract.stand_number : selectedContract.exhibition_name || selectedContract.template_name})
                    </span>
                  )}
                </label>
              </div>
            )}

            {/* To */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-500">Alıcı (To)</Label>
                <div className="flex gap-1">
                  <button onClick={() => setShowCc(s => !s)} className={`text-xs px-2 py-0.5 rounded ${showCc ? 'bg-gray-200 text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}>+ CC</button>
                  <button onClick={() => setShowBcc(s => !s)} className={`text-xs px-2 py-0.5 rounded ${showBcc ? 'bg-gray-200 text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}>+ BCC</button>
                </div>
              </div>
              <EmailChips emails={toEmails} onChange={setToEmails} placeholder="alici@firma.com — Enter ile ekle" testId="email-to-input" />
            </div>

            {showCc && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">CC</Label>
                <EmailChips emails={ccEmails} onChange={setCcEmails} placeholder="cc@ornek.com" testId="email-cc-input" />
              </div>
            )}

            {showBcc && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">BCC (Gizli)</Label>
                <EmailChips emails={bccEmails} onChange={setBccEmails} placeholder="bcc@ornek.com" testId="email-bcc-input" />
              </div>
            )}

            {/* Subject */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Konu</Label>
              <Input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder={hasContext ? "Referans seçtikten sonra otomatik dolar veya manuel girebilirsiniz" : "E-posta konusu"}
                data-testid="email-subject-input"
              />
            </div>

            {/* Body — plain textarea, backend wraps in HTML */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">İçerik</Label>
              <Textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder={hasContext && selectedRef === 'none' ? "Öncelikle yukarıdan bir sözleşme veya stand seçiniz" : "E-posta içeriğini yazın..."}
                rows={7}
                className="text-sm resize-none"
                data-testid="email-body-input"
              />
            </div>

            {/* File attachments */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-gray-500">Ekler</Label>
                <span className="text-xs text-gray-400">{attachedFiles.length}/{MAX_FILES} dosya</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileAdd}
                data-testid="email-file-input"
              />
              {attachedFiles.length > 0 && (
                <div className="space-y-1">
                  {attachedFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-xs">
                      <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="flex-1 truncate text-gray-700">{f.name}</span>
                      <span className="text-gray-400 shrink-0">{formatSize(f.size)}</span>
                      <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 ml-1" data-testid={`remove-attachment-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachedFiles.length >= MAX_FILES}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-dashed border-gray-300 text-gray-500 hover:border-teal-400 hover:text-teal-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed w-full justify-center"
                data-testid="add-attachment-btn"
              >
                <Paperclip className="w-3.5 h-3.5" />
                Dosya Ekle
                <span className="text-gray-400">· Maks {MAX_FILES} dosya · Toplam {MAX_TOTAL_MB}MB</span>
              </button>
            </div>

            {/* Signature preview */}
            {sigHtml && (
              <div className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                <p className="text-xs text-gray-400 mb-2">İmza (otomatik eklenecek)</p>
                <div className="text-xs" dangerouslySetInnerHTML={{ __html: sigHtml }} />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="email-modal-cancel">İptal</Button>
          <Button
            onClick={handleSend}
            disabled={sending || loading || !fromEmail}
            className="bg-teal-600 hover:bg-teal-700 gap-1.5"
            data-testid="email-send-btn"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Gönderiliyor...' : 'Gönder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
